import { useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/router';
import { PATHS } from '@/utils/constants';
import { AUTH_CONFIG } from '@/config/auth';

export const useAuthTokenManager = () => {
  const router = useRouter();
  const isActiveRef = useRef(true);
  const lastActivityRef = useRef(Date.now());
  const refreshTimerRef = useRef<number | null>(null);
  const activityTimerRef = useRef<number | null>(null);

  const refreshToken = useCallback(async () => {
    try {
      const response = await fetch('/api/refreshToken', {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        console.error('Failed to refresh the token.');
        router.push(PATHS.LOGIN);
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      router.push(PATHS.LOGIN);
    }
  }, [router]);

  const handleUserActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;

    if (!isActiveRef.current) {
      isActiveRef.current = true;
    }

    if (activityTimerRef.current) {
      clearTimeout(activityTimerRef.current);
    }

    activityTimerRef.current = window.setTimeout(() => {
      isActiveRef.current = false;
    }, 300000);
  }, []);

  const startTokenRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    const scheduleNextRefresh = () => {
      if (isActiveRef.current) {
        refreshToken();
        refreshTimerRef.current = window.setTimeout(
          scheduleNextRefresh,
          AUTH_CONFIG.TOKEN_REFRESH_INTERVAL_MS,
        );
      } else {
        refreshTimerRef.current = window.setTimeout(scheduleNextRefresh, 60000);
      }
    };

    refreshTimerRef.current = window.setTimeout(
      scheduleNextRefresh,
      AUTH_CONFIG.TOKEN_REFRESH_INTERVAL_MS,
    );
  }, [refreshToken]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      return;
    }

    const hideInactivityDialog =
      router.pathname === PATHS.LOGIN ||
      router.pathname === PATHS.SESSION_EXPIRED ||
      router.pathname === PATHS.REAUTH;

    if (hideInactivityDialog) {
      return;
    }

    AUTH_CONFIG.ACTIVITY_EVENTS.forEach((event: string) => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });

    startTokenRefresh();

    return () => {
      AUTH_CONFIG.ACTIVITY_EVENTS.forEach((event: string) => {
        window.removeEventListener(event, handleUserActivity);
      });

      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }

      if (activityTimerRef.current) {
        clearTimeout(activityTimerRef.current);
      }
    };
  }, [handleUserActivity, startTokenRefresh, router.pathname]);

  return {
    isActive: isActiveRef.current,
    lastActivity: lastActivityRef.current,
    refreshToken,
  };
};
